package dev.lingvo.site.util

import kotlinx.browser.document
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag

/**
 * Hands a blob to the browser as a download.
 *
 * The object URL is revoked in a `finally`, and the anchor removed with it. A page that leaks one
 * per click holds the whole blob in memory for the life of the tab, which for a translation bundle
 * is tens of megabytes nobody can free.
 */
internal fun downloadBlob(blob: Blob, fileName: String) {
    val url = URL.createObjectURL(blob)
    val body = document.body ?: run { URL.revokeObjectURL(url); error("The download couldn't be started.") }
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = url
    anchor.download = fileName
    anchor.style.setProperty("display", "none")
    body.appendChild(anchor)
    try {
        anchor.click()
    } finally {
        body.removeChild(anchor)
        URL.revokeObjectURL(url)
    }
}

/** The same, for text a page assembled itself rather than fetched. */
internal fun downloadText(text: String, fileName: String, mimeType: String = "text/plain;charset=utf-8") {
    downloadBlob(Blob(arrayOf<dynamic>(text), BlobPropertyBag(type = mimeType)), fileName)
}

/**
 * One CSV field.
 *
 * Quotes everything rather than deciding per value: a tool name is a bare word today and gains a
 * comma the day somebody adds one, and a CSV that is correct only for the current data is a bug with
 * a delay on it. A leading `=`, `+`, `-` or `@` is prefixed with a quote — spreadsheets treat those
 * as formulas, and an agent chooses its own tool names.
 */
internal fun csvField(value: String): String {
    val guarded = if (value.firstOrNull() in setOf('=', '+', '-', '@')) "'$value" else value
    return "\"" + guarded.replace("\"", "\"\"") + "\""
}

internal fun csvRow(values: List<String>): String = values.joinToString(",", postfix = "\n", transform = ::csvField)
